import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, ProgressLine, Tag, Initials, attTone } from "@/components/kit";
import { BarsChart, GOLD, GREY } from "@/components/Charts";
import { GoalDialog, ValueDialog } from "@/components/GoalForms";
import { ConfirmDialog } from "@/components/Dialogs";
import { companySales, ranking, teamOf, nameMap, pct } from "@/lib/selectors";
import { goalResult, METRICS } from "@/lib/tasks";
import { PROFILES, MONTH_LABEL } from "@/data/mock";
import { brl } from "@/lib/format";

const Icon = ({ icon: I, label, testId, onClick }) => <Button variant="ghost" size="icon" aria-label={label} data-testid={testId} onClick={onClick}><I className="h-4 w-4 text-gold-dark" /></Button>;

function SalesGoals({ cid, edit }) {
  const { state } = useStore();
  const company = state.companies.find((c) => c.id === cid);
  const total = companySales(state, cid);
  const rows = ranking(state, cid);
  const att = pct(total, company.salesGoal);
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Panel title="Meta de vendas da empresa" testId="company-sales-goal" action={<Icon icon={Pencil} label="Editar meta" testId="edit-company-goal" onClick={() => edit({ kind: "company", value: company.salesGoal })} />}>
        <p className="font-display text-4xl font-semibold" data-testid="company-goal-total">{brl(total)}</p>
        <p className="mt-1 text-sm text-neutral-500" data-testid="company-goal-target">de {brl(company.salesGoal)}</p>
        <div className="mt-5"><ProgressLine value={att} /></div>
        <div className="mt-3"><Tag tone={attTone(att)} testId="company-goal-att">{att}% atingido</Tag></div>
      </Panel>
      <Panel title="Metas individuais de vendas" subtitle="Meta x realizado no mês" className="lg:col-span-2" testId="individual-sales-goals">
        <BarsChart data={rows.map((r) => ({ nome: r.name.split(" ")[0], meta: r.goal, vendas: r.sales }))} xKey="nome" series={[{ key: "vendas", name: "Realizado", color: GOLD }, { key: "meta", name: "Meta", color: GREY }]} height={200} />
        <ul className="mt-4 divide-y divide-neutral-100">
          {rows.map((r) => (
            <li key={r.id} data-testid={`individual-goal-${r.id}`} className="flex items-center gap-3 py-3">
              <Initials name={r.name} />
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{r.name}</p><p className="text-xs text-neutral-500">{brl(r.sales)} de {brl(r.goal)}</p></div>
              <Tag tone={attTone(r.att)}>{r.att}%</Tag>
              <Icon icon={Pencil} label="Editar meta" testId={`edit-individual-goal-${r.id}`} onClick={() => edit({ kind: "person", id: r.id, name: r.name, value: r.goal })} />
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function GoalCard({ g, names, onEdit, onDelete }) {
  const { state } = useStore();
  const { value, att } = goalResult(state, g);
  return (
    <article data-testid={`goal-card-${g.id}`} className="rounded-xl border border-neutral-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><Tag tone={g.collaboratorId ? "gold" : "dark"}>{g.collaboratorId ? names[g.collaboratorId] : "Empresa"}</Tag><h3 className="mt-3 font-display text-lg font-semibold">{g.title}</h3><p className="text-xs text-neutral-500">{METRICS[g.metric]}</p></div>
        <div className="flex shrink-0"><Icon icon={Pencil} label="Editar" testId={`goal-edit-${g.id}`} onClick={onEdit} /><Icon icon={Trash2} label="Excluir" testId={`goal-delete-${g.id}`} onClick={onDelete} /></div>
      </div>
      <p className="mt-4 text-sm" data-testid={`goal-value-${g.id}`}><span className="font-display text-2xl font-semibold">{value}{g.unit === "%" ? "%" : ""}</span> <span className="text-neutral-500">de {g.target}{g.unit === "%" ? "%" : ` ${g.unit}`}</span></p>
      <div className="mt-3 flex items-center gap-3"><ProgressLine value={att} /><Tag tone={attTone(att)} testId={`goal-att-${g.id}`}>{att}%</Tag></div>
    </article>
  );
}

export default function OwnerGoals() {
  const { state, updateCompany, updateCollaborator, saveGoal, deleteGoal } = useStore();
  const cid = PROFILES.empresaria.companyId;
  const [edit, setEdit] = useState(null);
  const [dlg, setDlg] = useState(null);
  const goals = state.goals.filter((g) => g.companyId === cid);
  const names = nameMap(state);
  const saveValue = (v) => {
    if (edit.kind === "company") updateCompany(cid, { salesGoal: v });
    else updateCollaborator(edit.id, { goal: v });
    toast.success("Meta atualizada.");
    setEdit(null);
  };
  return (
    <div data-testid="owner-goals-page">
      <PageHeader eyebrow={MONTH_LABEL} title="Metas e resultados" subtitle="Metas de vendas e metas operacionais da empresa e de cada colaboradora, com atingimento atualizado automaticamente." />
      <Tabs defaultValue="vendas">
        <TabsList className="bg-neutral-100 p-1">
          <TabsTrigger value="vendas" data-testid="goals-tab-sales" className="data-[state=active]:bg-white">Metas de vendas</TabsTrigger>
          <TabsTrigger value="operacionais" data-testid="goals-tab-ops" className="data-[state=active]:bg-white">Metas operacionais</TabsTrigger>
        </TabsList>
        <TabsContent value="vendas" className="mt-6"><SalesGoals cid={cid} edit={setEdit} /></TabsContent>
        <TabsContent value="operacionais" className="mt-6">
          <div className="mb-5 flex justify-end"><Button className="font-semibold" onClick={() => setDlg({ type: "edit" })} data-testid="new-goal-button"><Plus className="mr-1 h-4 w-4" /> Nova meta operacional</Button></div>
          {!goals.length && <p className="text-sm text-neutral-400">Nenhuma meta operacional cadastrada.</p>}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="ops-goals-list">
            {goals.map((g) => <GoalCard key={g.id} g={g} names={names} onEdit={() => setDlg({ type: "edit", g })} onDelete={() => setDlg({ type: "del", g })} />)}
          </div>
        </TabsContent>
      </Tabs>
      <ValueDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} title={edit?.kind === "company" ? "Meta de vendas da empresa" : `Meta de ${edit?.name || ""}`} label="Meta mensal (R$)" initial={edit?.value} onSave={saveValue} testId="sales-goal-dialog" />
      <GoalDialog open={dlg?.type === "edit"} onOpenChange={(v) => !v && setDlg(null)} initial={dlg?.g} people={teamOf(state, cid)} onSave={(x) => { saveGoal({ ...x, id: dlg?.g?.id, companyId: cid }); toast.success("Meta salva."); setDlg(null); }} />
      <ConfirmDialog open={dlg?.type === "del"} onOpenChange={(v) => !v && setDlg(null)} title="Excluir meta?" description={dlg?.g?.title} testId="goal-delete-dialog" onConfirm={() => { deleteGoal(dlg.g.id); toast.success("Meta excluída."); }} />
    </div>
  );
}
